import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Check, Loader2, Focus, FileText, Sparkles, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { RoomBlockRegistryProvider, useRoomBlockRegistry } from "./room/RoomBlockRegistry";
import {
  useRoomLaunch, useInteractionInstance, useSubmitInstance, useCompleteInstance, useReopenInstance, exitRoom,
} from "@/lib/practiceQueries";
import { useMe } from "@/lib/rehearsalRoomQueries";
import ContentViewerRenderer from "./room/renderers/ContentViewerRenderer";
import ChecklistRenderer from "./room/renderers/ChecklistRenderer";
import GuidedPracticeRenderer from "./room/renderers/GuidedPracticeRenderer";
import ReflectionRenderer from "./room/renderers/ReflectionRenderer";
import SessionRoomRenderer from "./room/renderers/SessionRoomRenderer";
import FileRoomRenderer from "./room/renderers/FileRoomRenderer";
import RehearsalRecorderRenderer from "./room/renderers/RehearsalRecorderRenderer";
import AnnotatedTextRenderer from "./room/renderers/AnnotatedTextRenderer";
import WorkDocumentRoomView from "./room/WorkDocumentRoomView";

const RENDERER_COMPONENTS = {
  content_viewer: ContentViewerRenderer,
  checklist: ChecklistRenderer,
  guided_practice: GuidedPracticeRenderer,
  reflection: ReflectionRenderer,
  rehearsal_recorder: RehearsalRecorderRenderer,
  annotated_text: AnnotatedTextRenderer,
};

function resolveReturnPath(workspaceId, routeKey, layerKey) {
  if (routeKey === "practice_layer" && layerKey) {
    return `/rehearsal-room/w/${workspaceId}/practice/${layerKey}`;
  }
  if (routeKey === "sessions") {
    return `/rehearsal-room/w/${workspaceId}/sessions`;
  }
  if (routeKey === "files_index") {
    return `/rehearsal-room/w/${workspaceId}/files`;
  }
  if (routeKey === "feedback_detail") {
    return `/rehearsal-room/w/${workspaceId}/feedback`;
  }
  return `/rehearsal-room/w/${workspaceId}/home`;
}

/**
 * Master v5 Prompt 9 — the common Rehearsal Room Runtime shell.
 *
 * Route: /rehearsal-room/w/:workspaceId/room/:launchId (mounted OUTSIDE
 * ClientShell — no ordinary sidebar/topbar, per the master spec's
 * Workspace Portal / Room Runtime separation documented in
 * room_runtime_contracts.py). The RoomLaunchView is ALWAYS re-resolved
 * from current backend state (useRoomLaunch has no client-side cache
 * override) — a launch resolved a moment ago never keeps stale
 * capabilities if membership/instance state changed underneath it.
 */
export default function RoomShell() {
  const { workspaceId, launchId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { data: me } = useMe();
  const { data: view, isPending: viewPending, isError: viewError } = useRoomLaunch(workspaceId, launchId);

  const isInteraction = view?.primary_target?.type === "interaction_instance";
  const instanceId = isInteraction ? view.primary_target.id : null;
  const { data: instanceData, isPending: instancePending, isError: instanceError } = useInteractionInstance(workspaceId, instanceId);

  const submitInstance = useSubmitInstance(workspaceId, instanceId);
  const completeInstance = useCompleteInstance(workspaceId, instanceId);
  const reopenInstance = useReopenInstance(workspaceId, instanceId);

  const [focusMode, setFocusMode] = useState(false);
  const [exiting, setExiting] = useState(false);
  const [confirmMessage, setConfirmMessage] = useState(null);

  if (viewError) {
    return <RoomUnavailable workspaceId={workspaceId} reason="This Room link is invalid, expired, or no longer available to you." />;
  }
  if (viewPending || !view) {
    return (
      <section className="min-h-screen flex items-center justify-center bg-ink" data-testid="room-shell-loading">
        <p className="text-paper/70">Opening the Rehearsal Room…</p>
      </section>
    );
  }
  if (isInteraction && (instanceError || (!instancePending && !instanceData))) {
    return <RoomUnavailable workspaceId={workspaceId} reason="This exercise is no longer available to you." />;
  }

  return (
    <RoomBlockRegistryProvider>
      <RoomShellInner
        workspaceId={workspaceId}
        launchId={launchId}
        view={view}
        instanceId={instanceId}
        instanceData={instanceData}
        instancePending={isInteraction && instancePending}
        submitInstance={submitInstance}
        completeInstance={completeInstance}
        reopenInstance={reopenInstance}
        isCoach={me?.portal_role === "coach_admin"}
        focusMode={focusMode}
        setFocusMode={setFocusMode}
        exiting={exiting}
        setExiting={setExiting}
        confirmMessage={confirmMessage}
        setConfirmMessage={setConfirmMessage}
        navigate={navigate}
        currentPath={location.pathname}
      />
    </RoomBlockRegistryProvider>
  );
}

function RoomShellInner({
  workspaceId, launchId, view, instanceId, instanceData, instancePending, submitInstance, completeInstance, reopenInstance,
  isCoach, focusMode, setFocusMode, exiting, setExiting, confirmMessage, setConfirmMessage, navigate, currentPath,
}) {
  const { flushAll, subscribeDirty } = useRoomBlockRegistry();
  const [hasUnsaved, setHasUnsaved] = useState(false);

  useEffect(() => subscribeDirty(setHasUnsaved), [subscribeDirty]);

  // Navigation blocking applies ONLY while genuinely unsaved local state
  // exists — the instant every block's debounced autosave resolves,
  // hasUnsaved flips back to false and the browser is free to close/
  // navigate without a warning.
  useEffect(() => {
    function handler(e) {
      if (!hasUnsaved) return;
      e.preventDefault();
      e.returnValue = "";
    }
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [hasUnsaved]);

  const isDocument = view.primary_target.type === "work_document";
  const isSession = view.primary_target.type === "session_room";
  const isFileReview = view.primary_target.type === "file_asset";
  const rendererKey = view.renderer.key;
  const instance = instanceData?.instance;
  const isInstanceLocked = instance ? instance.state === "completed" : false;
  // The only reason an active (not-yet-completed), otherwise-accessible
  // instance/document would lack edit_blocks is a View as Client session
  // (assert_view_as_client_capabilities_are_read_only) — completion is
  // handled separately via isInstanceLocked/isDocumentSubmitted, never
  // conflated with this flag. A blockless-but-interactive renderer (e.g.
  // rehearsal_recorder, upload_media) never gets edit_blocks even for its
  // owner (practice_routes._instance_capabilities only adds edit_blocks
  // when the renderer supports_blocks) — but a real owner-and-active
  // session always gets AT LEAST one of edit_blocks/submit/complete, and
  // a true View as Client session gets none of the three, so checking
  // all three (not just edit_blocks) is required here.
  const canEditBlocks = view.capabilities.includes("edit_blocks");
  const canAct = canEditBlocks || view.capabilities.includes("submit") || view.capabilities.includes("complete");
  const isViewAsClient = !canAct && !isInstanceLocked;

  const doExit = useCallback(async (returnRoute) => {
    setExiting(true);
    try {
      if (isDocument) {
        // BlockCard's own onBlur -> stopEditing() flush path (unchanged,
        // accepted Prompt 8A engine) — blur whatever is focused first so
        // a still-open edit flushes before we navigate away.
        document.activeElement?.blur?.();
        await new Promise((res) => setTimeout(res, 150));
      } else {
        await flushAll();
      }
      const result = await exitRoom(workspaceId, launchId, returnRoute || currentPath);
      const path = resolveReturnPath(workspaceId, result?.return_target?.route_key, instanceData?.layer_key);
      navigate(path, { replace: true });
    } finally {
      setExiting(false);
    }
  }, [isDocument, flushAll, workspaceId, launchId, currentPath, instanceData?.layer_key, navigate, setExiting]);

  async function handleSubmit() {
    try {
      await submitInstance.mutateAsync();
      setConfirmMessage("submitted");
    } catch (err) {
      toast.error(err?.message || "Couldn't submit yet — check the steps above.");
    }
  }

  async function handleComplete() {
    await completeInstance.mutateAsync();
    setConfirmMessage("completed");
  }

  async function handleReopen() {
    await reopenInstance.mutateAsync();
    setConfirmMessage(null);
  }

  const renderTitle = instanceData?.assignment_title || (isDocument ? "Work Document" : isSession ? "Coaching Session" : "Rehearsal Room");
  // content_viewer never creates an InteractionAttempt (nothing is
  // submitted — see practice_repository.complete_instance) — an attempt
  // badge would be dishonest for it, so it is the one renderer that
  // never shows one.
  const attemptLabel = instance && rendererKey !== "content_viewer"
    ? instance.state === "completed"
      ? `Attempt #${instance.current_attempt_number} submitted`
      : instance.current_attempt_number > 0
        ? `Attempt #${instance.current_attempt_number + 1} in progress`
        : "Attempt #1 in progress"
    : null;

  return (
    // Layout fix ("Fix Embedded Zoom Frame Sizing + Sticky Session
    // Layout"): the isSession branch gets a real, bounded 100dvh
    // viewport (h-[100dvh] + overflow-hidden here, flex-1 min-h-0 at
    // every level down to SessionRoomRenderer) instead of the previous
    // min-h-screen/page-scroll behavior every other Room renderer still
    // uses unchanged below. That bounded box is what lets
    // SessionRoomRenderer split into a shrink-0 Zoom area + a flex-1
    // overflow-y-auto Resource area — the ONLY thing that needs to
    // scroll for a long workbook is that inner area, not the page.
    <div className={`bg-ink flex flex-col ${isSession ? "h-[100dvh] overflow-hidden" : "min-h-screen"}`} data-testid="room-shell">
      <header className="flex items-center justify-between gap-3 px-4 sm:px-6 py-3 border-b border-paper/10 shrink-0" data-testid="room-shell-header">
        <div className="flex items-center gap-3 min-w-0">
          <button
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => doExit()}
            disabled={exiting}
            className="flex items-center gap-1.5 text-paper/70 hover:text-paper text-sm shrink-0 disabled:opacity-50"
            data-testid="room-shell-save-exit-btn"
          >
            {exiting ? <Loader2 size={15} className="animate-spin" /> : <ArrowLeft size={15} />}
            Save &amp; Exit
          </button>
          <div className="min-w-0 border-l border-paper/10 pl-3">
            <p className="font-serif text-paper truncate" data-testid="room-shell-renderer-title">{renderTitle}</p>
            {attemptLabel && <p className="text-xs text-paper/50" data-testid="room-shell-attempt-badge">{attemptLabel}</p>}
          </div>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          {hasUnsaved && <span className="text-xs text-mustard" data-testid="room-shell-unsaved-indicator">Unsaved changes</span>}
          <button
            onClick={() => setFocusMode((v) => !v)}
            className={`flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-full transition-colors ${focusMode ? "bg-mustard text-ink" : "text-paper/60 hover:text-paper hover:bg-paper/10"}`}
            data-testid="room-shell-focus-toggle"
          >
            <Focus size={13} /> Focus Mode
          </button>
        </div>
      </header>

      {!focusMode && (
        <div className="flex items-center gap-4 px-4 sm:px-6 py-2 text-xs text-paper/50 border-b border-paper/5 shrink-0" data-testid="room-shell-status-strip">
          <span className="flex items-center gap-1.5" data-testid="room-shell-activity-status">
            <Sparkles size={12} /> {isDocument ? "Shared document" : isSession ? "Coaching session" : isFileReview ? "File review" : "Solo practice"}
          </span>
          {view.resources.length > 0 && (
            <span className="flex items-center gap-1.5" data-testid="room-shell-resources-status">
              <FileText size={12} /> {view.resources.length} linked resource{view.resources.length === 1 ? "" : "s"}
            </span>
          )}
        </div>
      )}

      <main className={isSession ? "flex-1 min-h-0 overflow-hidden px-4 sm:px-6 flex flex-col" : "flex-1 px-4 sm:px-6 py-8"}>
        <div className={isSession ? "max-w-4xl mx-auto w-full flex-1 min-h-0 flex flex-col py-4" : "max-w-2xl mx-auto"}>
          {instancePending ? (
            <p className="text-paper/60" data-testid="room-shell-content-loading">Loading exercise…</p>
          ) : (
            <div className={isSession ? "bg-paper rounded-xl p-6 flex-1 min-h-0 flex flex-col overflow-hidden" : "bg-paper rounded-xl p-6"} data-testid="room-shell-renderer-mount">
              {isDocument ? (
                <WorkDocumentRoomView workspaceId={workspaceId} docId={view.primary_target.id} isViewAsClient={isViewAsClient} />
              ) : isSession ? (
                <SessionRoomRenderer workspaceId={workspaceId} srpId={view.primary_target.id} />
              ) : isFileReview ? (
                <FileRoomRenderer workspaceId={workspaceId} fasId={view.primary_target.id} />
              ) : rendererKey === "content_viewer" ? (
                <ContentViewerRenderer instructions={instanceData?.instructions} />
              ) : RENDERER_COMPONENTS[rendererKey] ? (
                (() => {
                  const RendererComp = RENDERER_COMPONENTS[rendererKey];
                  return (
                    <RendererComp
                      workspaceId={workspaceId}
                      docId={instance?.companion_document_id}
                      instanceId={instanceId}
                      instructions={instanceData?.instructions}
                      isViewAsClient={isViewAsClient}
                      isInstanceLocked={isInstanceLocked}
                    />
                  );
                })()
              ) : (
                <p className="text-terracotta text-sm" data-testid="room-shell-unsupported-renderer">This exercise type isn't supported yet.</p>
              )}

              {confirmMessage && (
                <div className="mt-6 border border-ink-blue/30 bg-ink-blue/5 rounded-lg p-4 flex items-center justify-between gap-3" data-testid="room-shell-confirmation">
                  <p className="text-sm text-ink flex items-center gap-2"><Check size={15} className="text-ink-blue" /> {confirmMessage === "submitted" ? "Submitted." : "Marked as done."}</p>
                  {isCoach && confirmMessage === "submitted" && (
                    <button onMouseDown={(e) => e.preventDefault()} onClick={handleReopen} className="text-xs text-terracotta hover:text-terracotta/80 flex items-center gap-1" data-testid="room-shell-reopen-btn">
                      <RotateCcw size={12} /> Reopen for another attempt
                    </button>
                  )}
                </div>
              )}

              {!confirmMessage && !instancePending && !isDocument && instance && instance.state !== "completed" && (
                <div className="mt-6 flex justify-end gap-3">
                  {view.capabilities.includes("submit") && (
                    <button
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={handleSubmit}
                      disabled={submitInstance.isPending}
                      className="bg-terracotta text-paper text-sm font-medium px-4 py-2 rounded-md hover:bg-terracotta/90 disabled:opacity-50"
                      data-testid="room-shell-submit-btn"
                    >
                      {submitInstance.isPending ? "Submitting…" : "Submit"}
                    </button>
                  )}
                  {view.capabilities.includes("complete") && (
                    <button
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={handleComplete}
                      disabled={completeInstance.isPending}
                      className="bg-terracotta text-paper text-sm font-medium px-4 py-2 rounded-md hover:bg-terracotta/90 disabled:opacity-50"
                      data-testid="room-shell-complete-btn"
                    >
                      {completeInstance.isPending ? "Marking done…" : "Mark as done"}
                    </button>
                  )}
                </div>
              )}

              {!isDocument && instance?.state === "completed" && !confirmMessage && (
                <div className="mt-6 flex justify-end">
                  {isCoach && (
                    <button onMouseDown={(e) => e.preventDefault()} onClick={handleReopen} className="text-xs text-terracotta hover:text-terracotta/80 flex items-center gap-1" data-testid="room-shell-reopen-btn">
                      <RotateCcw size={12} /> Reopen for another attempt
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

function RoomUnavailable({ workspaceId, reason }) {
  return (
    <section className="min-h-screen flex items-center justify-center bg-ink px-6" data-testid="room-shell-unavailable">
      <div className="max-w-sm text-center">
        <p className="text-paper mb-4">{reason}</p>
        <a href={`/rehearsal-room/w/${workspaceId}/home`} className="text-mustard hover:text-mustard/80 text-sm font-medium" data-testid="room-shell-unavailable-home-link">
          Back to Home
        </a>
      </div>
    </section>
  );
}
